// @pages/account/Login.page.tsx

import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    renderRoute,
    routeChooseSubscriptionWithStatus,
    routeCreateAccount,
    routeHome,
    routePasswordResetRequest,
} from "@navigation/typedRoutes";
import { useAuthenticationClient } from "@contexts/AuthenticationClient.context";
import { useJwtController, useJwtStateHandles } from "@contexts/Jwt.context";
import { useLoadingController } from "@contexts/Loading.context";
import type { JwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import type { EmailDraft } from "@ps-bridge/models/brands/emailDraft";
import type { PasswordDraft } from "@ps-bridge/models/brands/passwordDraft";

import { ok, type Ok } from "@ps-bridge/ok";
import { useIoOptionState } from "@utils/useIoOptionState";
import LoginView from "@view/authentication/Login.view";
import { buildLoginViewProps } from "@ps-bridge/login.program";
import type { LoginKernel } from "@ps-bridge/contracts/login";
import { mkLoginController } from "@ps-bridge/controllers/login";
import { retryWithBackoff } from "@utils/effects/retryWithDelay";
import * as PSAuthRules from "@ps-bridge/models/core/authRules";
import * as PSErrorHandlers from "@ps-bridge/models/core/errorHandlers";
import * as PSOption from "@utils/Option";
import { pipe } from "fp-ts/function";
import { makeEmail } from "@ps-bridge/models/brands/email";
import { unwrap as unwrapEmailDraft } from "@ps-bridge/models/brands/emailDraft";
import { makePassword } from "@ps-bridge/models/brands/password";
import { unwrap as unwrapPasswordDraft } from "@ps-bridge/models/brands/passwordDraft";
import {
    makeJwtSubscriptionStatus,
    unwrap as unwrapJwtSubscriptionStatus,
} from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import { actionEffect, runJwtRefreshIfNeeded } from "@ps-bridge/jwtSession.program";
import { mkControllerAction, mkNavigate } from "@ps-bridge/capabilities";
import type { IOts, OptionTs, TaskTs } from "@ps-bridge/interop";
import type { ErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import type { HttpStatusCode } from "@ps-bridge/models/brands/httpStatusCode";
import {
    effectCatchAll,
    effectFail,
    ioEffect,
    taskChain,
    taskEitherChain,
    taskEitherFromEffect,
    taskEitherMap,
    taskEitherWithLoading,
    taskFromIO,
    taskOf,
} from '@utils/effects/branded';
import { useOnInit } from "@hooks/effects/useIoEffect";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import API_URL from "@utils/config";
import { toLoadingErrorInput } from "@utils/loadingError";

const handleLoginSuccessTask = (
    setIsTokenExpiring: (value: boolean) => IOts<Ok>,
    setSuccess: IOts<Ok>,
    navigate: ReturnType<typeof useNavigate>,
    acceptLoginStatus: (status: JwtSubscriptionStatus) => IOts<Ok>,
    subscriptionStatus: JwtSubscriptionStatus,
    loginEmail: string,
    redirectFromRoute: OptionTs<string>,
): TaskTs<Ok> =>
    taskChain(
        taskFromIO(setIsTokenExpiring(false)),
        () =>
            taskChain(
                taskFromIO(acceptLoginStatus(subscriptionStatus)),
                () =>
                    taskChain(
                        taskFromIO(setSuccess),
                        () =>
                            taskFromIO(
                                ioEffect(() => {
                                    const pendingEmailKey = "pendingEmailValidationEmail";
                                    const jwtRefreshDisabledKey = "jwtRefreshDisabled";
                                    if (typeof window !== "undefined" && window.localStorage) {
                                        const pendingEmail = window.localStorage.getItem(pendingEmailKey);
                                        // Recover from stale auth-disable state after a successful credentialed login.
                                        window.localStorage.removeItem(jwtRefreshDisabledKey);
                                        if (!pendingEmail) {
                                            window.localStorage.setItem("pendingEmailValidation", "false");
                                        } else if (pendingEmail !== loginEmail) {
                                            window.localStorage.removeItem(pendingEmailKey);
                                            window.localStorage.setItem("pendingEmailValidation", "false");
                                        }
                                    }
                                    const normalizedStatus = String(subscriptionStatus);
                                    const redirect = PSAuthRules.subscriptionRedirect(normalizedStatus);
                                    const redirected = PSOption.fold(redirectFromRoute, () => false, (target) => {
                                        navigate(target);
                                        return true;
                                    });
                                    if (!redirected) {
                                        if (redirect === "home") {
                                            navigate(renderRoute(routeHome));
                                        } else if (redirect === "chooseSubscription") {
                                            navigate(renderRoute(routeChooseSubscriptionWithStatus(subscriptionStatus)));
                                        } else {
                                            navigate(renderRoute(routeHome));
                                        }
                                    }
                                    return ok;
                                }),
                            ),
                    ),
            ),
    );

const getSafeRedirectTarget = (search: string): OptionTs<string> => {
    const value = new URLSearchParams(search).get("next");
    if (typeof value !== "string") {
        return PSOption.none();
    }
    if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/login")) {
        return PSOption.none();
    }
    return PSOption.some(value);
};

const getSsoErrorMessage = (search: string): OptionTs<string> => {
    const value = new URLSearchParams(search).get("sso_error");
    switch (value) {
        case "provider_denied":
            return PSOption.some("SSO sign-in was canceled.");
        case "explicit_link_required":
            return PSOption.some("A matching account exists. Sign in with your password to link SSO.");
        case "invite_or_provisioning_required":
            return PSOption.some("SSO sign-in is not enabled for this account yet.");
        case "temporarily_unavailable":
            return PSOption.some("SSO sign-in is temporarily unavailable.");
        case "invalid_callback":
            return PSOption.some("SSO sign-in could not be completed. Please try again.");
        default:
            return PSOption.none();
    }
};

const readLoginInputValue = (testId: string): string => {
    if (typeof document === "undefined") {
        return "";
    }
    const element = document.querySelector<HTMLInputElement>(`[data-testid="${testId}"]`);
    return element?.value ?? "";
};

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const authenticationClient = useAuthenticationClient();
    const { isTokenExpiring, subscriptionStatus } = useJwtStateHandles();
    const jwtController = useJwtController();
    const { setLoading, setSuccess, setError } = useLoadingController();

    const emailState = useIoOptionState<EmailDraft>(PSOption.none());
    const passwordState = useIoOptionState<PasswordDraft>(PSOption.none());
    const { read: email } = emailState;
    const { read: password } = passwordState;
    const redirectFromRoute = useMemo(() => getSafeRedirectTarget(location.search), [location.search]);
    const ssoErrorMessage = useMemo(() => getSsoErrorMessage(location.search), [location.search]);
    const googleSsoStartUrl = useMemo(() => {
        const nextQuery = PSOption.fold(
            redirectFromRoute,
            () => "",
            (target) => `?next=${encodeURIComponent(target)}`,
        );
        return `${API_URL}/v1/auth/google/start${nextQuery}`;
    }, [redirectFromRoute]);

    const logoUri = "/assets/ouroboros_logo_flat.png";

    const navigateAction = useMemo(
        () =>
            mkNavigate((route) =>
                ioEffect(() => {
                    navigate(renderRoute(route));
                    return ok;
                }),
            ),
        [navigate],
    );

    const handleAuthenticationError = useCallback(({
        type,
        message,
        statusCode,
    }: {
        type: string;
        message: ErrorMessageText;
        statusCode: OptionTs<HttpStatusCode>;
    }): IOts<Ok> => {
        const { message: formatted, info } = PSErrorHandlers.authenticationErrorMessage({
            type,
            message,
            statusCode,
        });
        return setError({ message: formatted, info });
    }, [setError]);

    const handleLogin = useCallback((inputEmail: string, inputPassword: string): TaskTs<Ok> => {
        const request = {
            email: makeEmail(inputEmail),
            password: makePassword(inputPassword),
        };
        return pipe(
            (() => {
                const baseLogin = authenticationClient.login(request);
                const loginAttempt = retryWithBackoff(baseLogin, 1, (error) => error.type === "ServerError");
                const resilientLogin = effectCatchAll(loginAttempt, (error) =>
                    error.type === "ServerError" ? authenticationClient.login(request) : effectFail(error),
                );
                return taskEitherWithLoading(
                    setLoading,
                    () =>
                        taskEitherChain(
                            taskEitherFromEffect(resilientLogin, (reason) => reason as { type: string; message: ErrorMessageText; statusCode: OptionTs<HttpStatusCode>; }),
                            (status) =>
                                taskEitherMap(
                                    taskEitherFromEffect(
                                        authenticationClient.refreshJwtToken,
                                        (reason) => reason as { type: string; message: ErrorMessageText; statusCode: OptionTs<HttpStatusCode>; },
                                    ),
                                    () => makeJwtSubscriptionStatus(status),
                                ),
                        ),
                    (error) =>
                        taskFromIO(
                            handleAuthenticationError({
                                type: error.type,
                                message: error.message,
                                statusCode: error.statusCode,
                            }),
                        ),
                    (status) =>
                        handleLoginSuccessTask(
                            isTokenExpiring.set,
                            setSuccess,
                            navigate,
                            (value) => actionEffect(jwtController.acceptLoginStatus(value)),
                            status,
                            inputEmail,
                            redirectFromRoute,
                        ),
                );
            })(),
        );
    }, [
        authenticationClient,
        handleAuthenticationError,
        isTokenExpiring,
        jwtController,
        navigate,
        redirectFromRoute,
        setLoading,
        setSuccess,
    ]);

    const submit = useCallback((): TaskTs<Ok> => {
        const currentEmail = readLoginInputValue("login-email");
        const currentPassword = readLoginInputValue("login-password");
        if (currentEmail.trim() !== "" && currentPassword !== "") {
            return handleLogin(currentEmail, currentPassword);
        }
        const credentials = pipe(
            email,
            PSOption.chain((emailValue) =>
                PSOption.map((passwordValue) => ({ emailValue, passwordValue }))(password),
            ),
        );
        return PSOption.fold(credentials, () => {
                return taskOf(ok);
            }, ({ emailValue, passwordValue }) => {
                return handleLogin(unwrapEmailDraft(emailValue), unwrapPasswordDraft(passwordValue));
            },);
    }, [email, handleLogin, password]);

    const runTaskWithError = useRunTaskWithError();
    const submitAction = useMemo(
        () =>
            mkControllerAction(
                ioEffect(() => {
                    runTaskWithError(submit());
                    return ok;
                }),
            ),
        [runTaskWithError, submit],
    );
    const onInitAction = useMemo(
        () =>
            mkControllerAction(
                ioEffect(() => {
                    const hasSsoError = PSOption.fold(ssoErrorMessage, () => false, (message) => {
                        setError(toLoadingErrorInput(message, PSOption.none()))();
                        return true;
                    });
                    if (hasSsoError) {
                        return ok;
                    }
                    runTaskWithError(runJwtRefreshIfNeeded(jwtController));
                    if (!isTokenExpiring.read) {
                        const status = subscriptionStatus.read;
                        const normalizedStatus = unwrapJwtSubscriptionStatus(status);
                        const redirect = PSAuthRules.subscriptionRedirect(normalizedStatus);
                        const redirected = PSOption.fold(redirectFromRoute, () => false, (target) => {
                            navigate(target);
                            return true;
                        });
                        if (!redirected) {
                            if (redirect === "home") {
                                navigate(renderRoute(routeHome));
                            } else if (redirect === "chooseSubscription") {
                                navigate(renderRoute(routeChooseSubscriptionWithStatus(status)));
                            }
                        }
                    }
                    return ok;
                }),
            ),
        [ssoErrorMessage, isTokenExpiring.read, jwtController, navigate, redirectFromRoute, runTaskWithError, setError, subscriptionStatus.read],
    );
    const controller = useMemo(
        () => mkLoginController({ submit: submitAction, onInit: onInitAction }),
        [onInitAction, submitAction],
    );

    const loginKernel: LoginKernel = useMemo(
        () => ({
            controller,
            auth: {
                email: {
                    read: emailState.read,
                    setSome: emailState.setSome,
                    setMaybe: emailState.setMaybe,
                },
                password: {
                    read: passwordState.read,
                    setSome: passwordState.setSome,
                    setMaybe: passwordState.setMaybe,
                },
            },
            session: {
                controller: jwtController,
            },
            navigation: {
                navigate: navigateAction,
                createAccountRoute: routeCreateAccount,
                passwordResetRoute: routePasswordResetRequest,
            },
        }),
        [
            controller,
            emailState.read,
            emailState.setMaybe,
            emailState.setSome,
            jwtController,
            navigateAction,
            passwordState.read,
            passwordState.setMaybe,
            passwordState.setSome,
        ],
    );

    const viewProps = useMemo(
        () =>
            buildLoginViewProps({
                kernel: loginKernel,
                logoUri: PSOption.some(logoUri),
                googleSsoStartUrl,
            }),
        [googleSsoStartUrl, loginKernel, logoUri],
    );

    useOnInit(viewProps.onInit);

    return (
        <LoginView {...viewProps} pageState="ready" ssoErrorMessage={ssoErrorMessage} />
    );
};

export default LoginPage;
